const DEFAULT_SITE_NAME = "HanyeLeo";
const DEFAULT_SITE_URL = "https://www.hanyeleo.com/";
const DEFAULT_DESCRIPTION =
  "Discover upcoming events, nightlife, and event galleries across East Africa with HanyeLeo.";
const DEFAULT_OG_IMAGE = "/apple-icon.png";
const NON_INDEXABLE_ENVIRONMENTS = new Set(["dev", "development", "uat", "test", "staging"]);

export const slugify = (value = "") =>
  value
    .toString()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");

export const stripHtml = (value = "") =>
  value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();

export const trimText = (value = "", max = 160) => {
  if (!value) return "";
  if (value.length <= max) return value;
  return `${value.slice(0, Math.max(0, max - 1)).trim()}…`;
};

export const getSiteUrl = () => {
  if (process.env.REACT_APP_SITE_URL) {
    return process.env.REACT_APP_SITE_URL.endsWith("/")
      ? process.env.REACT_APP_SITE_URL
      : `${process.env.REACT_APP_SITE_URL}/`;
  }

  return DEFAULT_SITE_URL;
};

export const getSiteEnvironment = () =>
  (process.env.REACT_APP_SITE_ENV || process.env.NODE_ENV || "development").toLowerCase();

export const isIndexableEnvironment = () => !NON_INDEXABLE_ENVIRONMENTS.has(getSiteEnvironment());

export const getDefaultRobots = () => (isIndexableEnvironment() ? "index,follow" : "noindex,nofollow");

export const buildAbsoluteUrl = (path = "/") => {
  try {
    return new URL(path, getSiteUrl()).toString();
  } catch (error) {
    return path;
  }
};

export const resolveImageUrl = (path) => {
  if (!path) {
    return buildAbsoluteUrl(DEFAULT_OG_IMAGE);
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  if (path.startsWith("/")) {
    return buildAbsoluteUrl(path);
  }

  const baseImageUrl = process.env.REACT_APP_BASE_IMAGE_URL || "";
  if (/^https?:\/\//i.test(baseImageUrl)) {
    return `${baseImageUrl}${path}`;
  }

  return buildAbsoluteUrl(`${baseImageUrl}${path}`);
};

export const buildEventPath = (eventOrId, explicitSlug) => {
  const eventId = typeof eventOrId === "object" ? eventOrId?.id : eventOrId;
  const eventTitle = typeof eventOrId === "object" ? eventOrId?.title : explicitSlug;
  const slug = slugify(eventTitle || "event");
  return `/event/${eventId}${slug ? `/${slug}` : ""}`;
};

export const buildLocationPath = (cityTitle) => {
  const slug = slugify(cityTitle || "");
  return slug ? `/${slug}` : "/";
};

export const matchesLocationSlug = (cityTitle, routeParam = "") => {
  const normalizedRoute = (routeParam || "").toLowerCase();
  return (
    normalizedRoute === (cityTitle || "").toLowerCase() ||
    normalizedRoute === slugify(cityTitle || "")
  );
};

export const buildEventDescription = (event) => {
  if (!event) return DEFAULT_DESCRIPTION;

  const description = trimText(stripHtml(event.description || ""), 140);
  if (description) return description;

  const details = [event.title, event.venue_name || event.location, event.date || event.event_date]
    .filter(Boolean)
    .join(" · ");

  return trimText(details || DEFAULT_DESCRIPTION, 155);
};

export const getDefaultSeo = () => ({
  siteName: DEFAULT_SITE_NAME,
  title: DEFAULT_SITE_NAME,
  description: DEFAULT_DESCRIPTION,
  image: buildAbsoluteUrl(DEFAULT_OG_IMAGE),
});

